import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Search, Star, Flame, Minus, Plus } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const VegMark = () => (
  <span
    title="Pure Veg"
    className="flex h-4 w-4 items-center justify-center rounded-[3px] border-2 border-[#4CAF50]"
  >
    <span className="h-1.5 w-1.5 rounded-full bg-[#4CAF50]" />
  </span>
);

const AddControl = ({ item }) => {
  const { items, add, dec } = useCart();
  const inCart = items.find((c) => c.name === item.name);

  if (item.available === false) {
    return (
      <button
        data-testid={`sold-out-${slug(item.name)}`}
        disabled
        className="w-full cursor-not-allowed rounded-full border border-red-500/30 bg-red-500/5 py-2 font-grotesk text-sm font-semibold text-red-400/70"
      >
        Sold Out
      </button>
    );
  }

  if (inCart) {
    return (
      <div className="flex items-center justify-between rounded-full border border-[#F57C00]/50 bg-[#F57C00]/10 px-2 py-1.5">
        <button
          data-testid={`cart-dec-${slug(item.name)}`}
          onClick={() => dec(item.name)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[#FFB74D] transition-colors hover:bg-[#F57C00]/20"
          aria-label={`Remove one ${item.name}`}
        >
          <Minus size={14} />
        </button>
        <span className="font-mono-accent text-sm font-semibold text-[#FFB74D]">{inCart.qty}</span>
        <button
          data-testid={`cart-inc-${slug(item.name)}`}
          onClick={() => add(item)}
          className="flex h-7 w-7 items-center justify-center rounded-full text-[#FFB74D] transition-colors hover:bg-[#F57C00]/20"
          aria-label={`Add one ${item.name}`}
        >
          <Plus size={14} />
        </button>
      </div>
    );
  }

  return (
    <button
      data-testid={`add-to-cart-${slug(item.name)}`}
      onClick={() => add({ name: item.name, price: item.price, image: item.image })}
      className="w-full rounded-full border border-[#F57C00]/50 py-2 font-grotesk text-sm font-semibold text-[#FF9800] transition-all duration-300 hover:bg-[#F57C00] hover:text-black"
    >
      Add to Order
    </button>
  );
};

const MenuSection = () => {
  const [data, setData] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/menu`)
      .then((res) => setData(res.data))
      .catch((err) => console.error("menu fetch failed", err));
  }, []);

  const categories = data?.categories ?? [];

  const items = useMemo(() => {
    const pool =
      activeCat === "all"
        ? categories.flatMap((c) => c.items.map((i) => ({ ...i, cat: c.title })))
        : (categories.find((c) => c.id === activeCat)?.items ?? []).map((i) => ({
            ...i,
            cat: categories.find((c) => c.id === activeCat)?.title,
          }));
    const q = query.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(
      (i) => i.name.toLowerCase().includes(q) || i.desc.toLowerCase().includes(q)
    );
  }, [categories, activeCat, query]);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[#F57C00]/5 blur-[120px]" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="text-center">
          <Eyebrow>The Menu</Eyebrow>
          <h2 className="mx-auto mt-4 max-w-3xl font-display text-3xl font-medium leading-tight tracking-tight sm:text-4xl md:text-5xl">
            From tava to table,
            <span className="text-gradient-saffron italic"> the full South Indian canon.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-zinc-400">
            Our complete pure-veg menu — every price straight from the kitchen
            card. Add dishes to your order and pay by UPI.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="mx-auto flex max-w-md items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 transition-colors focus-within:border-[#F57C00]/50">
            <Search size={16} className="shrink-0 text-zinc-500" />
            <input
              data-testid="menu-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search dosa, idli, thali, coffee..."
              className="w-full bg-transparent text-sm text-zinc-100 placeholder-zinc-500 outline-none"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-8">
          <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2">
            <button
              data-testid="menu-category-tab-all"
              onClick={() => setActiveCat("all")}
              className={`relative shrink-0 rounded-full px-5 py-2.5 font-grotesk text-sm font-medium transition-colors duration-300 ${
                activeCat === "all" ? "text-black" : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {activeCat === "all" && (
                <motion.span
                  layoutId="menu-pill"
                  className="absolute inset-0 rounded-full bg-[#F57C00]"
                  transition={{ type: "spring", bounce: 0.25, duration: 0.55 }}
                />
              )}
              <span className="relative z-10">All</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`menu-category-tab-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                className={`relative shrink-0 rounded-full px-5 py-2.5 font-grotesk text-sm font-medium transition-colors duration-300 ${
                  activeCat === c.id ? "text-black" : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {activeCat === c.id && (
                  <motion.span
                    layoutId="menu-pill"
                    className="absolute inset-0 rounded-full bg-[#F57C00]"
                    transition={{ type: "spring", bounce: 0.25, duration: 0.55 }}
                  />
                )}
                <span className="relative z-10">{c.title}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {!data ? (
          <div data-testid="menu-loading" className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card-surface h-72 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p data-testid="menu-empty" className="mt-16 text-center text-zinc-500">
            No dishes match your search — try "dosa" or "idli".
          </p>
        ) : (
          <motion.div layout className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {items.map((item) => {
                const soldOut = item.available === false;
                return (
                  <motion.article
                    layout
                    key={`${item.cat}-${item.name}`}
                    data-testid={`menu-item-${slug(item.name)}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35 }}
                    className={`card-surface group overflow-hidden rounded-2xl transition-all duration-300 hover:-translate-y-1.5 hover:border-[#F57C00]/40 hover:shadow-[0_20px_50px_rgba(0,0,0,0.55)] ${
                      soldOut ? "opacity-70" : ""
                    }`}
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        loading="lazy"
                        className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                          soldOut ? "grayscale" : ""
                        }`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute left-3 top-3">
                        <VegMark />
                      </div>
                      {soldOut ? (
                        <span
                          data-testid={`sold-out-badge-${slug(item.name)}`}
                          className="absolute right-3 top-3 rounded-full bg-red-600 px-3 py-1 font-mono-accent text-[10px] font-semibold uppercase tracking-wider text-white"
                        >
                          Sold Out
                        </span>
                      ) : (
                        item.badge && (
                          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-[#F57C00] px-3 py-1 font-mono-accent text-[10px] font-semibold uppercase tracking-wider text-black">
                            <Flame size={10} /> {item.badge}
                          </span>
                        )
                      )}
                      <span className="absolute bottom-3 left-3 rounded-full bg-black/60 px-2.5 py-1 font-mono-accent text-[10px] uppercase tracking-wider text-zinc-300 backdrop-blur">
                        {item.cat}
                      </span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-display text-xl font-semibold text-amber-50">
                          {item.name}
                        </h3>
                        <span className="shrink-0 font-mono-accent text-lg font-semibold text-[#FFB74D]">
                          ₹{item.price}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.desc}</p>
                      <div className="mt-4 flex items-center gap-3">
                        <div className="flex-1">
                          <AddControl item={item} />
                        </div>
                        {item.rating && !soldOut && (
                          <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#388E3C]/40 bg-[#388E3C]/10 px-2.5 py-1.5 text-xs text-[#81C784]">
                            <Star size={11} className="fill-[#81C784]" />
                            {item.rating}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default MenuSection;
